import React, { useState, useEffect, useMemo } from 'react';
import { pricesAPI } from '@/lib/api';
import { 
  Search, Plus, ChevronDown, ChevronRight, DollarSign,
  Calendar, Check, Loader2, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const SupplierPriceComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [addPriceDialog, setAddPriceDialog] = useState({ open: false, product: null });
  const [priceForm, setPriceForm] = useState({
    supplier: '',
    newSupplier: '',
    useNewSupplier: false,
    price: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await pricesAPI.getComparison();
      console.log('Comparación de precios response:', response.data);
      setData(response.data);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Error desconocido';
      toast.error(`Error al cargar comparación de precios: ${errorMsg}`);
      console.error('Error completo:', error);
      console.error('Response data:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!data?.products) return [];
    return data.products.filter(product => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      return (
        product.item_name.toLowerCase().includes(term) ||
        product.item_code.toLowerCase().includes(term)
      );
    });
  }, [data, searchTerm]);

  const toggleRow = (itemId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedRows(newExpanded);
  };

  const openAddPriceDialog = (product) => {
    setAddPriceDialog({ open: true, product });
    setPriceForm({
      supplier: '',
      newSupplier: '',
      useNewSupplier: false,
      price: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const handleAddPrice = async () => {
    const supplierToUse = priceForm.useNewSupplier 
      ? priceForm.newSupplier.trim() 
      : priceForm.supplier;
    
    if (!supplierToUse) {
      toast.error('Selecciona o ingresa un proveedor');
      return;
    }
    if (!priceForm.price || parseFloat(priceForm.price) <= 0) {
      toast.error('Ingresa un precio válido');
      return;
    }

    setSubmitting(true);
    try {
      await pricesAPI.addSupplierPrice(
        addPriceDialog.product.item_id,
        supplierToUse,
        parseFloat(priceForm.price),
        priceForm.date
      );
      toast.success(`Precio de ${supplierToUse} registrado`);
      setAddPriceDialog({ open: false, product: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar precio');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return `$${price.toLocaleString('es-CL')}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr + 'T00:00:00');
      return date.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const allSuppliers = data?.all_suppliers || [];

  return (
    <div className="space-y-4" data-testid="supplier-price-comparison">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Productos</span>
            <DollarSign className="w-5 h-5 text-slate-400" />
          </div>
          <p className="text-2xl font-bold text-slate-900 mt-1">{data?.total_products || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Con Precios</span>
            <Check className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600 mt-1">{data?.products_with_prices || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Proveedores</span>
            <Building2 className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-blue-600 mt-1">{allSuppliers.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Sin Precio</span>
            <DollarSign className="w-5 h-5 text-amber-500" />
          </div>
          <p className="text-2xl font-bold text-amber-600 mt-1">
            {(data?.total_products || 0) - (data?.products_with_prices || 0)}
          </p>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Buscar producto..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Comparison Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold w-8"></th>
                <th className="px-4 py-3 text-left font-semibold">Código</th>
                <th className="px-4 py-3 text-left font-semibold">Producto</th>
                {allSuppliers.map(supplier => (
                  <th key={supplier} className="px-4 py-3 text-right font-semibold min-w-[100px]">
                    <span className="truncate block max-w-[100px]" title={supplier}>
                      {supplier}
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredProducts.map((product) => {
                const isExpanded = expandedRows.has(product.item_id);
                const pricesBySup = product.prices_by_supplier || {};
                
                return (
                  <React.Fragment key={product.item_id}>
                    {/* Main Row */}
                    <tr className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        {Object.keys(pricesBySup).length > 0 && (
                          <button
                            onClick={() => toggleRow(product.item_id)}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-slate-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-500" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">{product.item_code}</td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-slate-900">{product.item_name}</p>
                        <p className="text-xs text-slate-400">{product.unit}</p>
                      </td>
                      {allSuppliers.map(supplier => {
                        const priceInfo = pricesBySup[supplier];
                        const isMinPrice = priceInfo && product.min_price_supplier === supplier;
                        
                        return (
                          <td 
                            key={supplier} 
                            className={`px-4 py-3 text-right font-medium ${
                              isMinPrice 
                                ? 'bg-green-50 text-green-700' 
                                : priceInfo 
                                  ? 'text-slate-900' 
                                  : 'text-slate-300'
                            }`}
                          >
                            {priceInfo ? (
                              <div>
                                <span className={isMinPrice ? 'font-bold' : ''}>
                                  {formatPrice(priceInfo.price)}
                                </span>
                                {isMinPrice && (
                                  <Check className="w-3 h-3 inline ml-1 text-green-600" />
                                )}
                              </div>
                            ) : '-'}
                          </td>
                        );
                      })}
                      <td className="px-4 py-3 text-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openAddPriceDialog(product)}
                          className="text-primary hover:text-primary/80"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                    
                    {/* Expanded Detail Row */}
                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={allSuppliers.length + 4} className="px-4 py-3">
                          <div className="pl-8">
                            <p className="text-xs font-semibold text-slate-500 mb-2">
                              Detalle de cotizaciones:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                              {Object.entries(pricesBySup).map(([supplier, info]) => (
                                <div 
                                  key={supplier}
                                  className={`flex items-center justify-between p-2 rounded-lg border ${
                                    product.min_price_supplier === supplier
                                      ? 'border-green-300 bg-green-50'
                                      : 'border-slate-200 bg-white'
                                  }`}
                                >
                                  <div>
                                    <p className="font-medium text-sm">{supplier}</p>
                                    <p className="text-xs text-slate-500 flex items-center gap-1">
                                      <Calendar className="w-3 h-3" />
                                      {formatDate(info.date)}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className={`font-bold ${
                                      product.min_price_supplier === supplier
                                        ? 'text-green-700'
                                        : 'text-slate-900'
                                    }`}>
                                      {formatPrice(info.price)}
                                    </p>
                                    {product.min_price_supplier === supplier && (
                                      <span className="text-xs text-green-600 font-medium">
                                        Más barato
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              
              {filteredProducts.length === 0 && (
                <tr>
                  <td colSpan={allSuppliers.length + 4} className="px-4 py-8 text-center text-slate-500">
                    No se encontraron productos
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Price Dialog */}
      <Dialog open={addPriceDialog.open} onOpenChange={(open) => !open && setAddPriceDialog({ open: false, product: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Precio de Proveedor</DialogTitle>
          </DialogHeader>
          
          {addPriceDialog.product && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{addPriceDialog.product.item_name}</p>
                <p className="text-sm text-slate-500">{addPriceDialog.product.item_code} • {addPriceDialog.product.unit}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                {!priceForm.useNewSupplier ? (
                  <div className="space-y-2">
                    <Select 
                      value={priceForm.supplier} 
                      onValueChange={(v) => setPriceForm({...priceForm, supplier: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar proveedor" />
                      </SelectTrigger>
                      <SelectContent>
                        {allSuppliers.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPriceForm({...priceForm, useNewSupplier: true})}
                      className="text-primary"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Agregar nuevo proveedor
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Nombre del nuevo proveedor"
                      value={priceForm.newSupplier}
                      onChange={(e) => setPriceForm({...priceForm, newSupplier: e.target.value})}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPriceForm({...priceForm, useNewSupplier: false})}
                    >
                      Seleccionar existente
                    </Button>
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Precio</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0"
                      value={priceForm.price}
                      onChange={(e) => setPriceForm({...priceForm, price: e.target.value})}
                      className="pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Fecha Cotización</Label>
                  <Input
                    type="date"
                    value={priceForm.date}
                    onChange={(e) => setPriceForm({...priceForm, date: e.target.value})}
                  />
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddPriceDialog({ open: false, product: null })}
            >
              Cancelar
            </Button>
            <Button onClick={handleAddPrice} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Guardando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar Precio
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupplierPriceComparison;
